import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import VolumeDownRoundedIcon from "@mui/icons-material/VolumeDownRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import { Box, Collapse, Slider, Typography } from "@mui/material";
import { AUDIO_CHANNEL_CONFIG } from "../constants/audioChannels";
import { AUDIO_TONES_BY_CHANNEL } from "../constants/audioCatalog";
import { AudioChannelPreference, AudioToneId, NormalizedAudioSettingsItem } from "../types";
import { useContactCenterTheme } from "../../../virtual-receptionist/theming/ContactCenterThemeContext";
import { AudioOptionCard } from "./AudioOptionCard";

type AudioSettingRowProps = {
  item: NormalizedAudioSettingsItem;
  preference?: AudioChannelPreference;
  expanded: boolean;
  onToggle: () => void;
  onToneChange: (value: AudioToneId) => void;
  onVolumeChange: (value: number) => void;
  onPreview: (value: AudioToneId) => void;
  activePreviewToneId: AudioToneId | null;
};

export function AudioSettingRow({
  item,
  preference,
  expanded,
  onToggle,
  onToneChange,
  onVolumeChange,
  onPreview,
  activePreviewToneId,
}: AudioSettingRowProps) {
  const { theme } = useContactCenterTheme();
  const config = AUDIO_CHANNEL_CONFIG[item.channel];
  const tones = AUDIO_TONES_BY_CHANNEL[item.channel];
  const toneId = preference?.toneId ?? tones[0].id;
  const volume = preference?.volume ?? 80;
  const selectedTone = tones.find((tone) => tone.id === toneId) ?? tones[0];
  const Icon = config.Icon;

  return (
    <Box
      sx={{
        borderRadius: "10px",
        border: `1px solid ${theme.shell.border}`,
        backgroundColor: theme.shell.bg,
        p: 1,
      }}
    >
      <Box
        onClick={(event) => {
          event.stopPropagation();
          onToggle();
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
        }}
      >
        <Icon sx={{ fontSize: 17, color: theme.shell.textPrimary, opacity: 0.72 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              color: theme.shell.textSecondary,
              fontSize: "0.62rem",
              fontWeight: 850,
              textTransform: "uppercase",
              lineHeight: 1.1,
            }}
          >
            {config.label}
          </Typography>
          <Typography sx={{ color: theme.shell.textPrimary, fontSize: "0.72rem", fontWeight: 800 }} noWrap>
            {selectedTone.label}
            {selectedTone.description ? ` (${selectedTone.description})` : ""}
          </Typography>
        </Box>
        <ExpandMoreRoundedIcon
          sx={{
            fontSize: 18,
            color: theme.shell.textSecondary,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.18s ease",
          }}
        />
      </Box>

      <Collapse in={expanded} timeout={160} unmountOnExit>
        <Box
          role="radiogroup"
          aria-label={config.label}
          onClick={(event) => event.stopPropagation()}
          sx={{ display: "grid", gap: 0.75, mt: 0.75 }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto",
              alignItems: "center",
              gap: 0.75,
              px: 0.75,
              py: 0.25,
            }}
          >
            <VolumeDownRoundedIcon sx={{ fontSize: 16, color: theme.shell.textSecondary }} />
            <Slider
              size="small"
              value={volume}
              min={0}
              max={100}
              aria-label={`${config.label} volume`}
              onChange={(_, nextValue) => onVolumeChange(Array.isArray(nextValue) ? nextValue[0] : nextValue)}
              sx={{
                color: theme.shell.accent,
                py: 0.75,
                "& .MuiSlider-root": {
                  height: 4,
                },
                "& .MuiSlider-thumb": {
                  width: 14,
                  height: 14,
                  boxShadow: "none",
                },
                "& .MuiSlider-track, & .MuiSlider-rail": {
                  height: 4,
                },
                "& .MuiSlider-track": {
                  border: "none",
                },
                "& .MuiSlider-rail": {
                  opacity: 0.35,
                },
              }}
            />
            <VolumeUpRoundedIcon sx={{ fontSize: 16, color: theme.shell.textSecondary }} />
          </Box>

          {tones.map((tone) => (
            <AudioOptionCard
              key={tone.id}
              tone={tone}
              selected={tone.id === toneId}
              previewing={tone.id === activePreviewToneId}
              onSelect={() => onToneChange(tone.id)}
              onPreview={() => onPreview(tone.id)}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
